import { isSupabaseConfigured, supabase } from "../lib/supabase";
import {
  formatAdminPrice,
  formatProductAmount,
  getCatalog,
  getDefaultCartAmountForProduct,
  getWeightStepKg,
  isWeightedProduct,
  loadCatalog,
} from "./catalog";
import { loadOrders, saveOrders } from "./orders";
import { consumeInventoryLots, loadInventoryLots, recordInventoryMovement } from "./inventory";
import { getAntiGaspiLinePricing } from "./antiGaspi";

const CART_ID_KEY = "almoune_active_cart_id";
const CART_CACHE_KEY = "almoune_cart_cache";
export const CART_RESERVATION_MINUTES = 20;

const generateId = (prefix) => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}${crypto.randomUUID()}`;
  }

  return `${prefix}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const getReservationDeadline = () =>
  new Date(Date.now() + CART_RESERVATION_MINUTES * 60 * 1000).toISOString();

const ensureProductOrderLimit = (product, nextQuantity) => {
  const maxPerOrder = Number(product?.maxPerOrder || 0);
  if (maxPerOrder > 0 && nextQuantity > maxPerOrder) {
    throw new Error(
      `Maximum ${maxPerOrder} ${isWeightedProduct(product) ? "kg" : "unite(s)"} autorise(es) pour ${product.name || "ce produit"}.`
    );
  }
};

const normalizeCartAmount = (product, amount) => {
  if (isWeightedProduct(product)) {
    const rawAmount = Number(amount || getDefaultCartAmountForProduct(product));
    const minWeight = Number(product?.minWeightKg || getDefaultCartAmountForProduct(product));
    const stepWeight = Number(getWeightStepKg(product));
    const normalizedAmount = Number(rawAmount.toFixed(3));

    if (normalizedAmount <= 0) {
      throw new Error("Le poids demande doit etre superieur a 0.");
    }

    if (normalizedAmount < minWeight) {
      throw new Error(
        `Poids minimum ${formatProductAmount(product, minWeight)} pour ${product.name || "ce produit"}.`
      );
    }

    const stepRatio = normalizedAmount / stepWeight;
    if (Math.abs(stepRatio - Math.round(stepRatio)) > 0.001) {
      throw new Error(
        `Le poids doit respecter un palier de ${formatProductAmount(product, stepWeight)}.`
      );
    }

    return normalizedAmount;
  }

  return Math.max(1, Math.round(Number(amount || 1)));
};

const readCartCache = () => {
  if (typeof window === "undefined") {
    return { id: "", items: [], reservedUntil: "" };
  }

  try {
    const raw = window.localStorage.getItem(CART_CACHE_KEY);
    if (!raw) {
      return { id: "", items: [], reservedUntil: "" };
    }

    const parsed = JSON.parse(raw);
    return {
      id: parsed.id || "",
      items: Array.isArray(parsed.items) ? parsed.items : [],
      reservedUntil: parsed.reservedUntil || "",
    };
  } catch (error) {
    return { id: "", items: [], reservedUntil: "" };
  }
};

const writeCartCache = (cart) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(CART_CACHE_KEY, JSON.stringify(cart));
    if (cart.id) {
      window.localStorage.setItem(CART_ID_KEY, cart.id);
    } else {
      window.localStorage.removeItem(CART_ID_KEY);
    }
    window.dispatchEvent(new CustomEvent("cart-updated"));
  }
  return cart;
};

const clearCartCache = () => {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(CART_CACHE_KEY);
    window.localStorage.removeItem(CART_ID_KEY);
    window.dispatchEvent(new CustomEvent("cart-updated"));
  }
};

const getStoredCartId = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return window.localStorage.getItem(CART_ID_KEY) || "";
};

const hasAuthenticatedSession = async () => {
  if (!isSupabaseConfigured || !supabase) {
    return false;
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  return Boolean(session?.user?.id);
};

const getSessionUserId = async () => {
  if (!isSupabaseConfigured || !supabase) {
    return "";
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  return session?.user?.id || "";
};

const getInventoryRow = async (productId) => {
  const { data, error } = await supabase
    .from("inventory")
    .select("id, product_id, stock_quantity, reserved_stock, location_label")
    .eq("product_id", productId)
    .eq("location_label", "Main stock")
    .is("variant_id", null)
    .maybeSingle();

  if (error || !data) {
    throw new Error("Stock introuvable pour ce produit.");
  }

  return data;
};

const updateInventoryReservation = async (inventoryRow, delta) => {
  const nextReserved = Number(inventoryRow.reserved_stock || 0) + Number(delta || 0);

  if (nextReserved < 0) {
    throw new Error("Le stock reserve ne peut pas devenir negatif.");
  }

  const { error } = await supabase
    .from("inventory")
    .update({ reserved_stock: nextReserved })
    .eq("id", inventoryRow.id);

  if (error) {
    throw error;
  }

  return {
    ...inventoryRow,
    reserved_stock: nextReserved,
  };
};

const ensureSupabaseCart = async () => {
  const existingId = getStoredCartId();
  const customerId = await getSessionUserId();

  if (existingId) {
    const { data } = await supabase
      .from("carts")
      .select("id, status, reserved_until")
      .eq("id", existingId)
      .eq("customer_id", customerId)
      .eq("status", "active")
      .maybeSingle();

    if (data) {
      return data;
    }
  }

  const newCartId = generateId("");
  const payload = {
    id: newCartId,
    customer_id: customerId,
    status: "active",
    reserved_until: getReservationDeadline(),
  };

  const { data, error } = await supabase
    .from("carts")
    .insert(payload)
    .select("id, status, reserved_until")
    .single();

  if (error) {
    throw error;
  }

  writeCartCache({ id: data.id, items: [], reservedUntil: data.reserved_until });
  return data;
};

const mapCartItemsWithCatalog = async (items) => {
  const catalog = await loadCatalog();
  const catalogMap = new Map(catalog.map((product) => [product.id, product]));

  return items.map((item) => {
    const product = catalogMap.get(item.product_id || item.productId) || {};
    const quantity = Number(item.quantity || 0);
    const pricing = getAntiGaspiLinePricing(product, quantity);
    const unitPrice =
      item.unit_price ?? item.unitPrice ?? pricing.blendedUnitPrice;

    return {
      id: item.id,
      cartId: item.cart_id || item.cartId,
      productId: item.product_id || item.productId,
      variantId: item.variant_id || item.variantId || null,
      title: product.name || item.product_name || "Produit",
      unit: isWeightedProduct(product)
        ? "kg"
        : product.variantLabel || product.unitType || "piece",
      price: unitPrice,
      priceLabel: formatAdminPrice(unitPrice),
      quantity,
      requestedWeightKg: Number(item.requested_weight_kg ?? item.requestedWeightKg ?? quantity),
      displayQuantity: isWeightedProduct(product)
        ? formatProductAmount(
            product,
            Number(item.requested_weight_kg ?? item.requestedWeightKg ?? quantity)
          )
        : `${quantity}`,
      reservedQuantity: Number(item.reserved_quantity ?? item.reservedQuantity ?? quantity),
      lineTotal: Number(item.line_total ?? item.lineTotal ?? unitPrice * quantity),
      lineTotalLabel: formatAdminPrice(
        Number(item.line_total ?? item.lineTotal ?? unitPrice * quantity)
      ),
      isWeighted: isWeightedProduct(product),
      product,
    };
  });
};

export const loadCart = async () => {
  if (!isSupabaseConfigured || !supabase || !(await hasAuthenticatedSession())) {
    const cached = readCartCache();
    const items = await mapCartItemsWithCatalog(cached.items || []);
    return {
      id: cached.id || "",
      items,
      reservedUntil: cached.reservedUntil || "",
    };
  }

  const cartId = getStoredCartId();
  if (!cartId) {
    return { id: "", items: [], reservedUntil: "" };
  }

  const customerId = await getSessionUserId();

  const { data: cart } = await supabase
    .from("carts")
    .select("id, status, reserved_until")
    .eq("id", cartId)
    .eq("customer_id", customerId)
    .maybeSingle();

  if (!cart || cart.status !== "active") {
    clearCartCache();
    return { id: "", items: [], reservedUntil: "" };
  }

  if (cart.reserved_until && new Date(cart.reserved_until) <= new Date()) {
    await releaseCart(cart.id);
    return { id: "", items: [], reservedUntil: "" };
  }

  const { data: items } = await supabase
    .from("cart_items")
    .select(
      "id, cart_id, product_id, variant_id, quantity, requested_weight_kg, reserved_quantity, unit_price, line_total, unit_type"
    )
    .eq("cart_id", cart.id)
    .order("created_at", { ascending: true });

  const mappedItems = await mapCartItemsWithCatalog(items || []);
  writeCartCache({
    id: cart.id,
    items: mappedItems,
    reservedUntil: cart.reserved_until || "",
  });

  return {
    id: cart.id,
    items: mappedItems,
    reservedUntil: cart.reserved_until || "",
  };
};

export const addProductToCart = async (product, quantity = 1) => {
  const normalizedProduct =
    product?.product || product || getCatalog().find((item) => item.id === product?.id);

  if (!normalizedProduct?.id) {
    throw new Error("Produit introuvable.");
  }

  if (!isSupabaseConfigured || !supabase || !(await hasAuthenticatedSession())) {
    const cached = readCartCache();
    const existingItem = cached.items.find((item) => item.productId === normalizedProduct.id);
    const requestedAmount = normalizeCartAmount(normalizedProduct, quantity);
    const nextQuantity = Number(existingItem?.quantity || 0) + requestedAmount;
    ensureProductOrderLimit(normalizedProduct, nextQuantity);
    const pricing = getAntiGaspiLinePricing(normalizedProduct, nextQuantity);
    const unitPrice = pricing.blendedUnitPrice;
    const nextItems = existingItem
      ? cached.items.map((item) =>
          item.productId === normalizedProduct.id
            ? {
                ...item,
                quantity: Number(item.quantity || 0) + requestedAmount,
                requestedWeightKg: isWeightedProduct(normalizedProduct)
                  ? Number(item.requestedWeightKg || 0) + requestedAmount
                  : null,
                reservedQuantity: Number(item.reservedQuantity || 0) + requestedAmount,
                unitPrice,
                lineTotal: pricing.lineTotal,
              }
            : item
        )
      : [
          ...cached.items,
          {
            id: generateId("cart-item-"),
            productId: normalizedProduct.id,
            quantity: requestedAmount,
            requestedWeightKg: isWeightedProduct(normalizedProduct) ? requestedAmount : null,
            reservedQuantity: requestedAmount,
            unitPrice,
            lineTotal: pricing.lineTotal,
          },
        ];

    return writeCartCache({
      id: cached.id || generateId("cart-"),
      items: nextItems,
      reservedUntil: getReservationDeadline(),
    });
  }

  const inventoryRow = await getInventoryRow(normalizedProduct.id);
  const requestedAmount = normalizeCartAmount(normalizedProduct, quantity);
  const availableStock =
    Number(inventoryRow.stock_quantity || 0) - Number(inventoryRow.reserved_stock || 0);

  if (availableStock < requestedAmount) {
    throw new Error("Stock insuffisant pour reserver ce produit.");
  }

  const cart = await ensureSupabaseCart();
  const { data: existingItem } = await supabase
    .from("cart_items")
    .select(
      "id, quantity, requested_weight_kg, reserved_quantity, unit_price, line_total, product_id, variant_id"
    )
    .eq("cart_id", cart.id)
    .eq("product_id", normalizedProduct.id)
    .is("variant_id", null)
    .maybeSingle();

  const nextQuantity = Number(existingItem?.quantity || 0) + requestedAmount;
  ensureProductOrderLimit(normalizedProduct, nextQuantity);
  const pricing = getAntiGaspiLinePricing(normalizedProduct, nextQuantity);
  const unitPrice = pricing.blendedUnitPrice;
  const lineTotal = pricing.lineTotal;

  await updateInventoryReservation(inventoryRow, requestedAmount);

  if (existingItem?.id) {
    await supabase
      .from("cart_items")
      .update({
        quantity: nextQuantity,
        requested_weight_kg: isWeightedProduct(normalizedProduct) ? nextQuantity : null,
        reserved_quantity: nextQuantity,
        unit_price: unitPrice,
        line_total: lineTotal,
      })
      .eq("id", existingItem.id);
  } else {
    await supabase.from("cart_items").insert({
      cart_id: cart.id,
      product_id: normalizedProduct.id,
      variant_id: null,
      quantity: requestedAmount,
      requested_weight_kg: isWeightedProduct(normalizedProduct) ? requestedAmount : null,
      reserved_quantity: requestedAmount,
      unit_price: unitPrice,
      line_total: lineTotal,
      unit_type: isWeightedProduct(normalizedProduct)
        ? "kg"
        : normalizedProduct.unitType || "piece",
    });
  }

  await supabase
    .from("carts")
    .update({ reserved_until: getReservationDeadline() })
    .eq("id", cart.id);

  await recordInventoryMovement({
    inventoryId: inventoryRow.id,
    productId: normalizedProduct.id,
    movementType: "reserve",
    quantity: requestedAmount,
    unitType: isWeightedProduct(normalizedProduct)
      ? "kg"
      : normalizedProduct.unitType || "piece",
    referenceType: "cart",
    referenceId: cart.id,
    reason: "Reservation depuis le panier client",
  });

  return loadCart();
};

export const updateCartItemQuantity = async (itemId, nextQuantity) => {
  if (!isSupabaseConfigured || !supabase || !(await hasAuthenticatedSession())) {
    const cached = readCartCache();
    const catalog = await loadCatalog();
    const nextItems = cached.items
      .map((item) => {
        if (item.id !== itemId) {
          return item;
        }

        const product = catalog.find((p) => p.id === item.productId) || item.product || {};
        const targetQuantity = normalizeCartAmount(product, nextQuantity);
        ensureProductOrderLimit(product, targetQuantity);
        const pricing = product?.id ? getAntiGaspiLinePricing(product, targetQuantity) : null;
        const unitPrice = pricing ? pricing.blendedUnitPrice : Number(item.unitPrice || 0);
        const lineTotal = pricing ? pricing.lineTotal : targetQuantity * unitPrice;
        return {
          ...item,
          quantity: targetQuantity,
          requestedWeightKg: isWeightedProduct(product) ? targetQuantity : null,
          reservedQuantity: targetQuantity,
          unitPrice,
          lineTotal,
        };
      })
      .filter(Boolean);

    return writeCartCache({
      ...cached,
      items: nextItems,
      reservedUntil: getReservationDeadline(),
    });
  }

  const { data: item } = await supabase
    .from("cart_items")
    .select(
      "id, cart_id, product_id, quantity, requested_weight_kg, reserved_quantity, unit_price, unit_type"
    )
    .eq("id", itemId)
    .maybeSingle();

  if (!item) {
    return loadCart();
  }

  const currentQuantity = Number(item.quantity || 0);
  const catalog = await loadCatalog();
  const product = catalog.find((entry) => entry.id === item.product_id);
  const targetQuantity = normalizeCartAmount(product, nextQuantity);
  ensureProductOrderLimit(product, targetQuantity);
  const pricing = product ? getAntiGaspiLinePricing(product, targetQuantity) : null;
  const unitPrice = pricing ? pricing.blendedUnitPrice : Number(item.unit_price || 0);
  const lineTotal = pricing ? pricing.lineTotal : targetQuantity * unitPrice;
  const delta = targetQuantity - currentQuantity;
  const inventoryRow = await getInventoryRow(item.product_id);
  const availableStock =
    Number(inventoryRow.stock_quantity || 0) - Number(inventoryRow.reserved_stock || 0);

  if (delta > 0 && availableStock < delta) {
    throw new Error("Stock insuffisant pour augmenter la quantite.");
  }

  if (delta !== 0) {
    await updateInventoryReservation(inventoryRow, delta);
    await recordInventoryMovement({
      inventoryId: inventoryRow.id,
      productId: item.product_id,
      movementType: delta > 0 ? "reserve" : "release",
      quantity: Math.abs(delta),
      unitType: item.unit_type || "piece",
      referenceType: "cart",
      referenceId: item.cart_id,
      reason: "Mise a jour de la quantite du panier",
    });
  }

  await supabase
    .from("cart_items")
    .update({
      quantity: targetQuantity,
      requested_weight_kg: isWeightedProduct(product) ? targetQuantity : null,
      reserved_quantity: targetQuantity,
      unit_price: unitPrice,
      line_total: lineTotal,
    })
    .eq("id", item.id);

  await supabase
    .from("carts")
    .update({ reserved_until: getReservationDeadline() })
    .eq("id", item.cart_id);

  return loadCart();
};

export const removeCartItem = async (itemId) => {
  if (!isSupabaseConfigured || !supabase || !(await hasAuthenticatedSession())) {
    const cached = readCartCache();
    return writeCartCache({
      ...cached,
      items: cached.items.filter((item) => item.id !== itemId),
    });
  }

  const { data: item } = await supabase
    .from("cart_items")
    .select("id, cart_id, product_id, reserved_quantity, unit_type")
    .eq("id", itemId)
    .maybeSingle();

  if (!item) {
    return loadCart();
  }

  const inventoryRow = await getInventoryRow(item.product_id);
  await updateInventoryReservation(inventoryRow, -Number(item.reserved_quantity || 0));
  await recordInventoryMovement({
    inventoryId: inventoryRow.id,
    productId: item.product_id,
    movementType: "release",
    quantity: Number(item.reserved_quantity || 0),
    unitType: item.unit_type || "piece",
    referenceType: "cart",
    referenceId: item.cart_id,
    reason: "Suppression d'une ligne panier",
  });

  await supabase.from("cart_items").delete().eq("id", item.id);
  return loadCart();
};

export const releaseCart = async (cartId = getStoredCartId()) => {
  if (!cartId) {
    clearCartCache();
    return { id: "", items: [], reservedUntil: "" };
  }

  if (!isSupabaseConfigured || !supabase || !(await hasAuthenticatedSession())) {
    clearCartCache();
    return { id: "", items: [], reservedUntil: "" };
  }

  const { data: items } = await supabase
    .from("cart_items")
    .select("id, cart_id, product_id, reserved_quantity, unit_type")
    .eq("cart_id", cartId);

  for (const item of items || []) {
    const inventoryRow = await getInventoryRow(item.product_id);
    await updateInventoryReservation(inventoryRow, -Number(item.reserved_quantity || 0));
    await recordInventoryMovement({
      inventoryId: inventoryRow.id,
      productId: item.product_id,
      movementType: "release",
      quantity: Number(item.reserved_quantity || 0),
      unitType: item.unit_type || "piece",
      referenceType: "cart",
      referenceId: cartId,
      reason: "Expiration ou vidage du panier",
    });
  }

  await supabase.from("cart_items").delete().eq("cart_id", cartId);
  await supabase
    .from("carts")
    .update({ status: "expired", reserved_until: new Date().toISOString() })
    .eq("id", cartId);
  clearCartCache();

  return { id: "", items: [], reservedUntil: "" };
};

export const clearCurrentCart = async () => releaseCart();

export const convertCartToOrder = async ({
  clientName,
  clientPhone = "",
  clientEmail = "",
  billingAddressId = null,
  shippingAddressId = null,
  deliveryZoneId = null,
  deliveryDriverId = null,
  deliveryDriverName = "",
  deliveryType = "Standard",
  deliveryFee = 0,
  paymentMethod = "",
  note = "",
}) => {
  const cart = await loadCart();

  if (!cart.id || cart.items.length === 0) {
    throw new Error("Panier vide.");
  }

  if (!isSupabaseConfigured || !supabase || !(await hasAuthenticatedSession())) {
    const orderId = `#AL-${Date.now().toString().slice(-6)}`;
    const total = cart.items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
    const nextOrders = [
      {
        id: orderId,
        orderNumber: orderId.replace("#", ""),
        businessUnit: "alimentation",
        client: clientName || "Client Almoune",
        phone: clientPhone,
        email: clientEmail,
        date: new Date().toISOString().slice(0, 10),
        items: cart.items.map((item) => ({
          name: item.title,
          quantity: item.quantity,
          soldWeightKg: item.isWeighted ? item.requestedWeightKg : null,
          unitPrice: item.price,
          lineTotal: item.lineTotal,
          unitType: item.product.unitType || "piece",
        })),
        status: "En attente",
        paymentStatus: "En attente",
        dispatchStatus: "Non preparee",
        deliveryType,
        subtotal: total,
        deliveryFee,
        amount: total + Number(deliveryFee || 0),
        paymentMethod,
        note,
      },
      ...(await loadOrders()),
    ];
    await saveOrders(nextOrders);
    clearCartCache();
    return {
      orderId,
      orderNumber: orderId.replace("#", ""),
      total: total + Number(deliveryFee || 0),
    };
  }

  const orderId = `order-${Date.now()}`;
  const orderNumber = `AL-${Date.now().toString().slice(-6)}`;
  const subtotal = cart.items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
  const totalAmount = subtotal + Number(deliveryFee || 0);
  const customerId = await getSessionUserId();

  for (const item of cart.items) {
    const inventoryRow = await getInventoryRow(item.productId);
    const productLots = await loadInventoryLots(item.productId);
    const hasTrackedLots = productLots.some((lot) => Number(lot.quantity || 0) > 0);
    const nextStock =
      Number(inventoryRow.stock_quantity || 0) - Number(item.reservedQuantity || 0);
    const nextReserved =
      Number(inventoryRow.reserved_stock || 0) - Number(item.reservedQuantity || 0);

    if (nextStock < 0 || nextReserved < 0) {
      throw new Error(`Stock invalide pour ${item.title}.`);
    }

    await supabase
      .from("inventory")
      .update({
        stock_quantity: nextStock,
        reserved_stock: nextReserved,
      })
      .eq("id", inventoryRow.id);

    if (hasTrackedLots) {
      await consumeInventoryLots({
        productId: item.productId,
        quantity: Number(item.reservedQuantity || 0),
        unitType: item.isWeighted ? "kg" : item.product.unitType || "piece",
        referenceId: orderId,
        reason: `Vente convertie en commande ${orderNumber}`,
      });
    } else {
      await recordInventoryMovement({
        inventoryId: inventoryRow.id,
        productId: item.productId,
        movementType: "sale",
        quantity: Number(item.reservedQuantity || 0),
        unitType: item.isWeighted ? "kg" : item.product.unitType || "piece",
        referenceType: "order",
        referenceId: orderId,
        reason: `Vente convertie en commande ${orderNumber}`,
      });
    }
  }

  let usedNormalizedSchema = false;

  try {
      const { error: orderInsertError } = await supabase.from("orders").insert({
        id: orderId,
        order_number: orderNumber,
        business_unit: "alimentation",
        customer_name: clientName || "Client Almoune",
        customer_phone: clientPhone,
        customer_email: clientEmail,
        customer_id: customerId || null,
        cart_id: cart.id,
        address_id: shippingAddressId,
        billing_address_id: billingAddressId,
        shipping_address_id: shippingAddressId,
        delivery_zone_id: deliveryZoneId,
        delivery_slot_id: null,
        status: "pending",
        payment_status: "pending",
        subtotal,
      delivery_fee: Number(deliveryFee || 0),
      total_amount: totalAmount,
      currency: "EUR",
      dispatch_status: "not_dispatched",
      delivery_label: deliveryType,
      note,
    });

    if (orderInsertError) {
      throw orderInsertError;
    }

    for (const item of cart.items) {
      await supabase.from("order_items").insert({
        order_id: orderId,
        product_id: item.productId,
        variant_id: item.variantId,
        product_name: item.title,
        variant_name: item.product.variantLabel || "",
        quantity: item.quantity,
        unit_type: item.isWeighted ? "kg" : item.product.unitType || "piece",
        sold_weight_kg: item.isWeighted ? Number(item.requestedWeightKg || item.quantity || 0) : null,
        unit_price: item.price,
        line_total: item.lineTotal,
      });
    }

    await supabase.from("payments").insert({
      order_id: orderId,
      provider: paymentMethod === "PayPal" ? "paypal" : "manual",
      amount: totalAmount,
      status: "pending",
      payment_method: paymentMethod,
    });

    usedNormalizedSchema = true;
  } catch (normalizedInsertError) {
    const existingOrders = await loadOrders();
    await saveOrders([
      {
        id: orderId,
        orderNumber,
        client: clientName || "Client Almoune",
        phone: clientPhone,
        email: clientEmail,
        date: new Date().toISOString().slice(0, 10),
        items: cart.items.map((item) => ({
          name: item.title,
          quantity: item.quantity,
          soldWeightKg: item.isWeighted ? item.requestedWeightKg : null,
          unitPrice: item.price,
          lineTotal: item.lineTotal,
          unitType: item.product.unitType || "piece",
        })),
        status: "En attente",
        paymentStatus: "En attente",
        dispatchStatus: "Non preparee",
        deliveryType,
        subtotal,
        deliveryFee,
        amount: totalAmount,
        paymentMethod,
        note,
      },
      ...existingOrders,
    ]);
  }

  await supabase
    .from("carts")
    .update({ status: "converted" })
    .eq("id", cart.id);
  await supabase.from("cart_items").delete().eq("cart_id", cart.id);
  clearCartCache();

  return {
    orderId,
    orderNumber,
    total: totalAmount,
    normalizedSchema: usedNormalizedSchema,
  };
};

export const getCartStepForProduct = (product) =>
  isWeightedProduct(product) ? getWeightStepKg(product) : 1;

export const getSuggestedCartAmount = (product) =>
  getDefaultCartAmountForProduct(product);
